import React from "react";
import {
  TableHead,
  TableRow,
  TableCell,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ColumnFilter from "./FilterComponents/ColumnFilter";

const TableHeader = ({
  columns,
  filterManager,
  renderHeaderCell = null,
  sortBy = [],
  onSortChange = null,
}) => {
  const theme = useTheme();

  const getNextSortBy = (field) => {
    const entry = sortBy.find((s) => s === field || s === `-${field}`);
    if (entry === field) return [`-${field}`]; // asc -> desc
    if (entry === `-${field}`) return []; // desc -> clear
    return [field]; // not sorted -> asc
  };

  return (
    <TableHead>
      <TableRow>
        {columns.map((column, index) => (
          <TableCell
            key={index}
            align={column.align || "left"}
            sx={{
              backgroundColor: "#F7F7FC",
              textAlign: column.align || "left",
              padding: "8px",
              width: column.width ? `${column.width} !important` : "auto",
              minWidth: column.width ? `${column.width} !important` : "auto",
              maxWidth: column.width ? `${column.width} !important` : "auto",
            }}
          >
            {renderHeaderCell && renderHeaderCell(column) != null ? (
              <>{renderHeaderCell(column)}</>
            ) : !column?.filterConfig ? (
              (() => {
                const isSortable =
                  (column.sortable || column.sortConfig?.sortable) &&
                  !!onSortChange;
                const ascEntry = sortBy.find((s) => s === column.field);
                const descEntry = sortBy.find((s) => s === `-${column.field}`);
                const isSorted = !!ascEntry || !!descEntry;
                const direction = ascEntry ? "asc" : descEntry ? "desc" : null;

                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        column.align === "center"
                          ? "center"
                          : column.align === "right"
                            ? "flex-end"
                            : "flex-start",
                      width: "100%",
                      cursor: isSortable ? "pointer" : "default",
                    }}
                    onClick={
                      isSortable
                        ? () => onSortChange(getNextSortBy(column.field))
                        : undefined
                    }
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={theme.customColors.text.strong}
                      sx={{ lineHeight: 1.4 }}
                    >
                      {column.label}
                    </Typography>
                    {isSortable && (
                      <Box sx={{ ml: 0.5, mb: 0.2, display: "flex", alignItems: "center" }}>
                        {direction === "asc" && (
                          <ArrowUpward
                            sx={{
                              fontSize: 18,
                              color: theme.customColors.text.strong,
                            }}
                          />
                        )}
                        {direction === "desc" && (
                          <ArrowDownward
                            sx={{
                              fontSize: 18,
                              color: theme.customColors.text.strong,
                            }}
                          />
                        )}
                        {!direction && (
                          <ArrowUpward
                            sx={{
                              fontSize: 18,
                              color: "#9CA3AF",
                              opacity: 0.85,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      column.align === "center"
                        ? "center"
                        : column.align === "right"
                          ? "flex-end"
                          : "flex-start",
                    minHeight: "40px",
                    width: "100%",
                    // Centred columns anchor the filter to the edge instead of
                    // letting it sit in the flow — see below.
                    position: column.align === "center" ? "relative" : "static",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={theme.customColors.text.strong}
                    sx={{
                      lineHeight: 1.4,
                      // The gap to the filter would also pull a centred label
                      // off-centre, so it only applies when the filter is inline.
                      paddingRight: column.align === "center" ? 0 : "4px",
                      width: column.filterConfig ? "auto" : "100%",
                    }}
                  >
                    {column.label}
                  </Typography>
                  {column.filterConfig && (
                    <Box
                      sx={{
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        // In a centred column the label must sit on the column's
                        // true centre, because the cell below centres its content
                        // alone. Left in the flow, the filter is centred together
                        // with the label and shoves it ~18px to the left — half of
                        // the icon box plus its gap — so every centred header
                        // reads as misaligned with the data under it. Taking it
                        // out of the flow fixes the label; the icon still sits at
                        // the right edge, where it does for other columns.
                        ...(column.align === "center" && {
                          position: "absolute",
                          right: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }),
                      }}
                    >
                      <ColumnFilter
                        column={column}
                        onFilterChange={filterManager.handleFilterChange}
                        filters={filterManager.getFilters(column.field)}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default TableHeader;
